import {
  TimePoint,
  TimePointType,
  TimeRange,
} from '../../../api/timeranges/types';
import { Timestamp } from '../../../types';

export interface MenuHandle {
  opened: boolean;
  anchorEl: ParentNode | null;
  open: () => void;
  close: () => void;
  setAnchorEl: (el: ParentNode | null) => void;
}

export enum Granularity {
  // These should be the same as moment's precision
  MONTH = 'month',
  YEAR = 'year',
  FINQUARTER = 'finquarter',
  FINYEAR = 'finYear',
}

export enum Months {
  January = 'January',
  February = 'February',
  March = 'March',
  April = 'April',
  May = 'May',
  June = 'June',
  July = 'July',
  August = 'August',
  September = 'September',
  October = 'October',
  November = 'November',
  December = 'December',
}

export enum Presets {
  LAST3MONTHS = 'last3Months',
  LAST6MONTHS = 'last6Months',
  LAST12MONTHS = 'last12Months',
  YTD = 'ytd',
  LASTYEAR = 'lastYear',
  FYTD = 'fytd', // Financial Year to Date
  LASTFINANCIALYEAR = 'lastFinancialYear',
  ALLTIME = 'allTime',
}

export interface TimeSliderGranularity {
  id: string;
  label: string;
  value: Granularity;
}

export interface TimeSliderPreset {
  id: Presets;
  label: string;
}

export interface GranularityInfo {
  defaultPreset: TimeSliderPreset;
  availablePresets: TimeSliderPreset[];
  granularity: TimeSliderGranularity;
}

type TimeSliderRangeUpdateActionType = 'update-range';
type TimeSliderGranularityActionType = 'update-granularity';
type TimeSliderResetActionType = 'reset';
type TimeSliderPresetActionType = 'update-preset';
type TimeSliderPointUpdateActionType = 'update-point';

export interface TimeSliderResetAction {
  type: TimeSliderResetActionType;
}

export interface TimeSliderPresetAction {
  type: TimeSliderPresetActionType;
  granularity: TimeSliderGranularity;
  preset: TimeSliderPreset;
  current: Timestamp;
}

export interface TimeSliderRangeUpdateAction {
  type: TimeSliderRangeUpdateActionType;
  start: TimePointType;
  end: TimePointType;
}

export interface TimeSliderGranularityAction {
  type: TimeSliderGranularityActionType;
  granularity: TimeSliderGranularity;
  current: Timestamp;
}

// TODO: ideally we should use a type parameter to ensure that all time ranges are of the same underlying type (e.g. Date, number or Quarter)
export interface TimeSliderState {
  start: TimePointType;
  end: TimePointType;
  range: TimeRange;
  initialRange: TimeRange;
  selectedGranularity: TimeSliderGranularity;
  granularities: TimeSliderGranularity[];
  selectedPreset: TimeSliderPreset;
  availableTimeRanges: Record<Granularity, TimeRange>;
  initialState: Omit<TimeSliderState, 'initialState'>;
  granularityInfo: Record<Granularity, GranularityInfo>;
  timePoints: TimePoint[];
  // Timepoints are used to know how time values for different granularities
  // map with each other. For example, it tells us what the year granularity
  // value for some monthly granularity value is.
}

export type TimeSliderAction =
  | TimeSliderRangeUpdateAction
  | TimeSliderGranularityAction
  | TimeSliderResetAction
  | TimeSliderPresetAction;

export type TimeSliderHandle = [
  TimeSliderState,
  React.Dispatch<TimeSliderAction>,
];

export type TimeSliderReducer<
  S extends TimeSliderState,
  A extends TimeSliderAction,
> = (state: S, action: A) => S;

// TimePointSelector

type TimePointSelectorUpdateActionType = 'update-point';
type TimePointSelectorResetActionType = 'reset';
export interface TimePointSelectorUpdateAction {
  type: TimePointSelectorUpdateActionType;
  date: TimePointType;
}
export interface TimePointSelectorResetAction {
  type: TimePointSelectorResetActionType;
}
export interface TimePointSelectorState {
  date: TimePointType;
  range: TimeRange;
  selectedGranularity: TimeSliderGranularity;
  granularities: TimeSliderGranularity[];
  availableTimeRanges: Record<Granularity, TimeRange>;
  timePoints: TimePoint[];
  initialState: Omit<TimePointSelectorState, 'initialState'>;
}

export type TimePointSelectorAction =
  | TimePointSelectorUpdateAction
  | TimePointSelectorResetAction;

export type TimePointSelectorHandle = [
  TimePointSelectorState,
  React.Dispatch<TimePointSelectorAction>,
];

export type TimePointSelectorReducer<
  S extends TimeSliderState,
  A extends TimeSliderAction,
> = (state: S, action: A) => S;

export enum TimeSliderTypes {
  RANGE = 'range',
  POINT = 'point',
}
